/**
 * The glossary (UX pass §3, decision 0056): the one place the words of the project views come from. Plain words the
 * user already uses: Milestone (never Release, Bet or Workstream), Plan, Team, Needs you, Outputs. Pure; no React.
 * Frozen during the parallel tracks of waves B1 and B2: a track that needs a new word keeps it next to its view and
 * INTEGRATION folds it in here. Wave B2 (decision 0057) adds the card, chat, review and setup words and the helpers for
 * times, repeats and health.
 */
import type { Proposal } from "@minga/core"
import { type AgentKind, typeDefOf } from "@minga/protocol"
import type { GlyphState } from "@/components/ui/status-dot"
import { clockTime } from "@/lib/time"
import type { Agent, ItemStatus } from "./view-types"

export const WORDS = {
  milestone: "Milestone",
  milestones: "Milestones",
  notInMilestone: "Not in a milestone",
  needsYou: "Needs you",
  plan: "Plan",
  team: "Team",
  outputs: "Outputs",
  runs: "Runs",
  workers: "Workers",
  // ---- decision 0086 ----
  /** The workspace's place for people pages and guidelines (not a project). */
  knowledge: "Knowledge",
  /** A page in Knowledge: `people/<handle>.md` or `guidelines/<topic>.md`; it applies to every project. */
  workspacePage: "Workspace page",
  /** `areas/<slug>/status.md`, kept by the area's lead agent. */
  areaStatus: "Area status",
  verbs: { decide: "Decide", answer: "Answer", review: "Review", allow: "Allow", do: "Do", sort: "Sort" },
  capture: "Capture",
  showOnPlan: "Show on the plan",
  // ---- wave B2 (decision 0057) ----
  kinds: {
    plan: "Plan",
    briefEdit: "Brief edit",
    question: "Question",
    review: "Review",
    permission: "Permission",
    stuck: "Stuck",
    memory: "Memory",
    outward: "Outward",
    monitor: "Monitor", // wave C (decision 0071)
  },
  whileAway: "While you were away",
  worked: "Worked",
  saved: "Saved",
  proposed: "Proposed",
  notSaved: "Not saved",
  why: "Why",
  ifApprove: "If you approve",
  askedBecause: "Asked because",
  change: "change",
  details: "Details",
  more: "More",
  yourAnswer: "Your answer",
  answer: "Answer",
  answerAndClose: "Answer and close",
  // ---- decision 0101: honest close labels (the close form) ----
  /** What a done item delivered, as the close form offers it. */
  closeResults: {
    verified: { label: "Verified", help: "the result was checked" },
    report_delivered: { label: "Report delivered", help: "the deliverable is a report or finding" },
    no_data: { label: "No data", help: "there was nothing to find; it says so" },
  },
  whatItDelivered: "What it delivered",
  /** Cancel because something outside the team blocks it (`blocked_external`). */
  blockedOutside: "Blocked by something outside",
  answerInWords: "Or answer in your words",
  pickThis: "Pick this",
  notThis: "Not this",
  undo: "Undo",
  approveRound: "Approve the round",
  requestChanges: "Request changes",
  merge: "Merge",
  approveWithoutMerging: "Approve without merging",
  allowOnce: "Allow once",
  deny: "Deny",
  retry: "Retry",
  pairRunner: "Pair a runner",
  addKey: "Add a key",
  connectedApps: "Your agents",
  checks: "Checks",
  waitingForYou: "Waiting for you",
  waitingForRunner: "waiting for a runner",
  connectedTools: "connected tools",
  milestonesAtOnce: "Milestones at once",
  address: "Address",
  startProject: "Start a project",
} as const

/** "Ada's", "Jess'": a name's possessive; "the lead's" without a name. */
export const possessiveOf = (name: string): string => {
  const n = name.trim() || "the lead"
  return n.endsWith("s") ? `${n}'` : `${n}'s`
}

/** "Ada's instructions" (the glossary's word for an agent's charter, in its name; decision 0166 §4: no fixed name). */
export const instructionsOf = (name: string): string => `${possessiveOf(name)} instructions`

/** "Where Ada runs"; "Where the lead runs" without a name. */
export const whereRunsOf = (name: string): string => `Where ${name.trim() || "the lead"} runs`

/**
 * Where an agent thinks, in one wording on every page (UX pass phase 2, P15). Agents think; workers run. A cloud mind
 * is "in the cloud (Anthropic key)" ("workspace" or "its own" when the key matters); a runner mind is "on <runner>"
 * or "on <whose> runners". Names are just names ("Lea", never "Lea (agent)"); the handle shows in mono only where
 * it matters (her page's header, Settings › Agents).
 */
export const cloudWhere = (provider: string, key?: "workspace" | "own" | undefined): string =>
  `in the cloud (${key === "own" ? "its own " : key === "workspace" ? "workspace " : ""}${
    provider === "openai" ? "OpenAI" : "Anthropic"
  } key)`

/** "Thinks in the cloud (Anthropic key)" from "in the cloud (Anthropic key)": the verb before a `…Where` phrase. */
export const thinksWhere = (where: string, capital = false): string =>
  `${capital ? "Thinks" : "thinks"} ${where}`

/** The first letter up ("In the cloud (Anthropic key)"). */
/** A run's CLI in short ("W-3 · Codex", "V-1 · Claude reviewer"); Antigravity is decision 0109. */
export const DRIVER_NAME: Readonly<Record<AgentKind, string>> = {
  claude: "Claude",
  codex: "Codex",
  antigravity: "Antigravity",
  muse: "Muse",
}

export const upperFirst = (s: string): string => (s ? `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}` : s)

/**
 * The one line each that tells the two inboxes apart (UX pass phase 2, P13): the bell says what is new; Needs you
 * holds what still waits on you.
 */
export const INBOX_LINES = {
  bell: "Updates and replies: finished work, answers and news. What's waiting on you is under Needs you.",
  needs:
    "What's waiting on you: decisions, answers, reviews and your to-dos. The bell tells you when something new happens.",
} as const

/** The planning statuses as words (decision 0084 §1.5): Planning (not started, never dispatched), Ready, Now. */
export const planningWord = (s: "backlog" | "todo" | "in_progress"): "Planning" | "Ready" | "Now" =>
  s === "backlog" ? "Planning" : s === "todo" ? "Ready" : "Now"

const STATUS_WORDS: Record<ItemStatus, string> = {
  triage: "To sort",
  backlog: "Planning",
  todo: "Ready",
  in_progress: "In progress",
  in_review: "In review",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
}

/** An item's status as a person reads it: To sort · Planning · Ready · In progress · In review · Blocked · Done · Cancelled. */
export const itemStatusWord = (s: ItemStatus): string => STATUS_WORDS[s]

/** The plannable types that read as one word, "Milestone". */
export const MILESTONE_TYPES: ReadonlyArray<string> = ["release", "bet", "workstream"]

/** A type key as a word: release, bet and workstream → "Milestone"; others the registry label; unknown capitalized. */
export const typeWord = (type: string): string =>
  MILESTONE_TYPES.includes(type)
    ? WORDS.milestone
    : (typeDefOf(type)?.label ?? (type ? `${type[0]?.toUpperCase() ?? ""}${type.slice(1)}` : "Item"))

/** "4 of 6 done"; nothing to count → "nothing yet". */
export const progressText = (p: { readonly done: number; readonly total: number }): string =>
  p.total === 0 ? "nothing yet" : `${p.done} of ${p.total} done`

/** Lea's state as the header chip says it (decision 0056 §5.1): a glyph and one lowercase word. */
export const leaWord = (state: Agent["state"]): { readonly text: string; readonly glyph: GlyphState } => {
  switch (state) {
    case "thinking":
    case "working":
      return { text: "working", glyph: "live" }
    case "idle":
      return { text: "idle", glyph: "wait" }
    case "queued":
      return { text: "queued", glyph: "wait" }
    case "needs_attention":
      return { text: "can't work", glyph: "blocked" }
    case "away":
      return { text: "away", glyph: "wait" }
    case "paused":
      return { text: "paused", glyph: "wait" }
  }
}

// ---- wave B2 helpers (decision 0057) ----------------------------------------------------------------------------

/**
 * The card's kind tag from the proposal alone (Home has no state): a plan whose only changes are ReviseBrief → "Brief
 * edit"; a question with an escalation → "Stuck"; a human review card (intent `review:<item>:<round>`) → "Review".
 */
export const cardKindWord = (p: Pick<Proposal, "body" | "intentKey">): string => {
  const b = p.body
  switch (b._tag) {
    case "plan_change":
      return b.changes.every((c) => c._tag === "ReviseBrief") ? WORDS.kinds.briefEdit : WORDS.kinds.plan
    case "question":
      if (b.escalation) return WORDS.kinds.stuck
      return p.intentKey.startsWith("review:") ? WORDS.kinds.review : WORDS.kinds.question
    case "permission":
      return WORDS.kinds.permission
    case "memory":
      return WORDS.kinds.memory
    case "outward_action":
      return WORDS.kinds.outward
    case "monitor":
      return WORDS.kinds.monitor
    case "project_request":
      return "Project work request"
  }
}

/** Lea's status as a sentence-case phrase (Home cards, Team, the chat header): "Idle", "Working on DIC-6", "Can't work". */
export const leaStatusWord = (state: Agent["state"], workingOn: string | null): string => {
  switch (state) {
    case "thinking":
    case "working":
      return workingOn ? `Working on ${workingOn}` : "Working"
    case "idle":
      return "Idle"
    case "queued":
      return "Queued"
    case "needs_attention":
      return "Can't work"
    case "away":
      return "Waiting for a runner"
    case "paused":
      return "Paused"
  }
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const

const dateWords = (at: number, now: number): string => {
  const d = new Date(at)
  const day = `${MONTHS[d.getMonth()]} ${d.getDate()}`
  return d.getFullYear() === new Date(now).getFullYear() ? day : `${day}, ${d.getFullYear()}`
}

/**
 * A time in words, never with seconds: under an hour "just now" / "12 min", under a day "3 h", under a week "Tue 09:10",
 * then "Oct 10" this year and "Oct 10, 2025" before. Future times: "in 12 min", "in 3 h", then the same dates.
 */
export const whenText = (at: number, now: number): string => {
  const diff = now - at
  const abs = Math.abs(diff)
  if (abs < 45_000) return "just now"
  if (abs < HOUR) {
    const n = Math.max(1, Math.round(abs / MINUTE))
    return diff >= 0 ? `${n} min` : `in ${n} min`
  }
  if (abs < DAY) {
    const n = Math.round(abs / HOUR)
    return diff >= 0 ? `${n} h` : `in ${n} h`
  }
  if (abs < 7 * DAY) return `${WEEKDAYS[new Date(at).getDay()]} ${clockTime(at)}`
  return dateWords(at, now)
}

/** `whenText` for the past with "ago" on the relative forms: "6 min ago", "3 h ago"; "just now" and dates as they are. */
export const sinceText = (at: number, now: number): string => {
  const w = whenText(at, now)
  return /^\d+ (?:min|h)$/.test(w) ? `${w} ago` : w
}

const CRON_DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const

const DAY_NAMES = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
] as const

/**
 * A repeat in words for the common crons: "Mondays at 9:00", "Daily at 9:00", "Weekdays at 9:00"; anything else "on a
 * schedule (0 9 1 * *)". The time is in the repeat's own time zone; UTC says so.
 */
export const repeatWords = (cron: string, tz: string): string => {
  const parts = cron.trim().split(/\s+/)
  const [min, hour, dom, mon, dow] = parts
  const fallback = `on a schedule (${cron.trim()})`
  if (parts.length !== 5 || !/^\d{1,2}$/.test(min ?? "") || !/^\d{1,2}$/.test(hour ?? "")) return fallback
  if (dom !== "*" || mon !== "*") return fallback
  const at = `${Number(hour)}:${String(Number(min)).padStart(2, "0")}${tz === "UTC" || tz === "Etc/UTC" ? " UTC" : ""}`
  // Day names as cron writes them too: SUN–SAT (any case) are 0–6.
  const day = (dow ?? "")
    .toUpperCase()
    .replace(/\b(SUN|MON|TUE|WED|THU|FRI|SAT)\b/g, (d) =>
      String(CRON_DAYS.indexOf(d as (typeof CRON_DAYS)[number])),
    )
  if (day === "*") return `Daily at ${at}`
  if (day === "1-5") return `Weekdays at ${at}`
  if (/^[0-7]$/.test(day)) return `${DAY_NAMES[Number(day) % 7]} at ${at}`
  return fallback
}

const HEALTH_WORDS = { on_track: "on track", at_risk: "at risk", off_track: "off track" } as const

/** "on track · Jannik, Tue 09:10": a health with who set it and when. */
export const healthWords = (health: keyof typeof HEALTH_WORDS, by: string, at: number, now: number): string =>
  `${HEALTH_WORDS[health]} · ${by}, ${whenText(at, now)}`

/**
 * Words the views of this wave never show (the guard test renders them and fails on any of these). Matched as whole
 * words, case-sensitive for the capitalized ones.
 */
export const FORBIDDEN_WORDS: ReadonlyArray<string> = [
  "Release",
  "Workstream",
  "Bet",
  "Backlog",
  "Triage",
  "My work",
  "Roadmap",
  "Graph",
  "Woken",
  "mind",
  "lease",
  "sink",
  "pack",
  "tok",
  "closed",
  // wave B2 (decision 0057)
  "Temporary agents",
  "temporary",
  "Charter",
  "Slug",
  "ESCALATION",
  "NEEDS YOU",
  "non retryable",
  "Approve this one",
  "Approve all",
  "Needs approval",
  "needs attention",
  "Picked up",
]

/** Decision 0086 §6.2: the tag on a card that proposes a workspace page. */
export const WORKSPACE_PAGE_TAG = `${WORDS.workspacePage} · applies to every project`
